/**
 * Shown in place of a page's content when the org's plan doesn't include it.
 *
 * Deliberately not a 404 and not a redirect — the URL, the sidebar, and the
 * back button all keep working, so the page reads as "not on your plan yet"
 * rather than "broken". The contact button mails the platform owner through
 * an Edge Function; the recipient address never reaches the browser.
 */
import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Lock, Check, LayoutDashboard, ClipboardList, Briefcase, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useRequestPlanUpgrade } from '@/hooks/use-plan-access'
import { useToast } from '@/stores'
import {
  mainNavItems, cmmsNavItems, planningNavItems, managementNavItems,
  salesNavItems, toolsNavItems,
} from '@/components/layout/nav-items'

const ALL_NAV_ITEMS = [
  ...mainNavItems, ...cmmsNavItems, ...planningNavItems,
  ...managementNavItems, ...salesNavItems, ...toolsNavItems,
]

/** Name this page the way the sidebar does, so the copy matches what they clicked. */
function featureNameFor(pathname: string): string | null {
  const match = ALL_NAV_ITEMS
    .filter((item) => pathname === item.href || pathname.startsWith(`${item.href}/`))
    // Longest href wins so /work-orders/archive beats /work-orders.
    .sort((a, b) => b.href.length - a.href.length)[0]
  return match?.title ?? null
}

const INCLUDED = [
  { title: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { title: 'Work Orders', href: '/work-orders', icon: ClipboardList },
  { title: 'Vendor Portal', href: '/portal/vendor', icon: Briefcase },
]

export function PlanLockedPage() {
  const { pathname } = useLocation()
  const toast = useToast()
  const requestUpgrade = useRequestPlanUpgrade()
  const [sent, setSent] = useState(false)

  const featureName = useMemo(() => featureNameFor(pathname), [pathname])

  const handleContact = () => {
    requestUpgrade.mutate(
      { page: featureName ?? undefined, path: pathname },
      {
        onSuccess: () => {
          setSent(true)
          toast.success(
            'Request sent',
            'Our team will reach out with more information shortly.',
          )
        },
        onError: (err: unknown) => {
          toast.error(
            'Could not send your request',
            (err as Error)?.message ?? 'Please try again in a moment.',
          )
        },
      },
    )
  }

  return (
    <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
      <Card className="w-full max-w-lg">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col items-center text-center">
            <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Lock className="h-6 w-6 text-primary" aria-hidden="true" />
            </div>

            <h1 className="text-xl font-semibold sm:text-2xl">
              {featureName ? `${featureName} isn't part of your plan` : 'This page isn\'t part of your plan'}
            </h1>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              Your account includes the dashboard, work orders, and the vendor portal.
              {featureName ? ` ${featureName} is` : ' This is'} available on a paid plan —
              reach out and we'll walk you through what it does.
            </p>
          </div>

          <div className="mt-7 rounded-lg border bg-muted/40 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Included with your account
            </p>
            <ul className="mt-3 space-y-2">
              {INCLUDED.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="flex items-center gap-3 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
                  >
                    <item.icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">{item.title}</span>
                    <Check className="h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 flex flex-col gap-2 sm:flex-row-reverse">
            <Button
              className="w-full sm:w-auto sm:flex-1"
              onClick={handleContact}
              disabled={requestUpgrade.isPending || sent}
            >
              {requestUpgrade.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {sent ? 'Request sent' : 'Contact support'}
            </Button>
            <Button variant="outline" className="w-full sm:w-auto sm:flex-1" asChild>
              <Link to="/dashboard">Back to dashboard</Link>
            </Button>
          </div>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            {sent
              ? "We've passed your details along — someone will follow up by email."
              : "We'll email you about what's included. No obligation."}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
